import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { Icon } from '@iconify/react';

interface ConfirmationModalProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onConfirm: () => void;
    title?: string;
    message: string;
    description?: string;
    confirmText?: string;
    cancelText?: string;
    confirmColor?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
    confirmVariant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'ghost';
    confirmClassName?: string;
    cancelVariant?: 'solid' | 'bordered' | 'light' | 'flat' | 'faded' | 'shadow' | 'ghost';
    icon?: string;
    iconColor?: string;
    isLoading?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
}

export function ConfirmationModal({
    isOpen,
    onOpenChange,
    onConfirm,
    title = 'Confirm Action',
    message,
    description,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmColor = 'danger',
    confirmVariant = 'solid',
    confirmClassName,
    cancelVariant = 'light',
    icon,
    iconColor = 'text-warning',
    isLoading = false,
    size = 'md',
}: ConfirmationModalProps) {
    const handleConfirm = () => {
        onConfirm();
        onOpenChange(false);
    };

    const handleCancel = () => {
        onOpenChange(false);
    };

    return (
        <Modal isOpen={isOpen} onOpenChange={onOpenChange} size={size}>
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            <div className="flex items-center gap-3">
                                {icon && <Icon icon={icon} className={`h-6 w-6 ${iconColor}`} />}
                                {title}
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <p className="text-foreground">{message}</p>
                            {description && (
                                <p className="text-small text-default-500">{description}</p>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                variant={cancelVariant}
                                onPress={() => {
                                    handleCancel();
                                    onClose();
                                }}
                                isDisabled={isLoading}
                            >
                                {cancelText}
                            </Button>
                            <Button
                                color={confirmColor}
                                variant={confirmVariant}
                                className={confirmClassName}
                                onPress={() => {
                                    handleConfirm();
                                    onClose();
                                }}
                                isLoading={isLoading}
                            >
                                {confirmText}
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
}
